import React from 'react';
import { motion } from 'framer-motion';

// Lotus Icon - Symbol of purity and wellness
export const LotusIcon = ({ className = '', animate = true }) => {
  const IconComponent = animate ? motion.svg : 'svg';
  
  const motionProps = animate ? {
    initial: { scale: 0, rotate: -180 },
    animate: { scale: 1, rotate: 0 },
    transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] },
    whileHover: { scale: 1.1, rotate: 5 }
  } : {};

  return (
    <IconComponent
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      {...motionProps}
    >
      <path d="M12 2C8 2 5 5 5 9c0 2 1 4 3 5-2 1-3 3-3 5 0 4 3 7 7 7s7-3 7-7c0-2-1-4-3-5 2-1 3-3 3-5 0-4-3-7-7-7z" />
      <path d="M12 9c-2-2-4-1-4 1s2 3 4 1" />
      <path d="M12 9c2-2 4-1 4 1s-2 3-4 1" />
      <circle cx="12" cy="12" r="1" fill="currentColor" />
    </IconComponent>
  );
};

// Leaf Icon - Natural healing
export const LeafIcon = ({ className = '', animate = true }) => {
  const IconComponent = animate ? motion.svg : 'svg';
  
  const motionProps = animate ? {
    initial: { scale: 0, rotate: -90 },
    animate: { scale: 1, rotate: 0 },
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] },
    whileHover: { scale: 1.1, rotate: -5 }
  } : {};

  return (
    <IconComponent
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      {...motionProps}
    >
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </IconComponent>
  );
};

// Herb Icon - Medicinal plants
export const HerbIcon = ({ className = '', animate = true }) => {
  const IconComponent = animate ? motion.svg : 'svg';
  
  const motionProps = animate ? {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
    whileHover: { scale: 1.05 }
  } : {};

  return (
    <IconComponent
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      {...motionProps}
    >
      <path d="M7 20h10" />
      <path d="M10 20c5.5-2.5.8-6.4 3-10" />
      <path d="M9.5 9.4c1.1.8 1.8 2.2 2.3 3.7-2 .4-3.5.4-4.8-.3-1.2-.6-2.3-1.9-3-4.2 2.8-.5 4.4 0 5.5.8z" />
      <path d="M14.1 6a7 7 0 0 0-1.1 4c1.9-.1 3.3-.7 4.3-1.4 1-1.1 1.6-2.7 1.7-4.6-2.7.1-4 1-4.9 2z" />
    </IconComponent>
  );
};

// Wellness Drop Icon - Essential oils/medicine
export const WellnessDropIcon = ({ className = '', animate = true }) => {
  const IconComponent = animate ? motion.svg : 'svg';
  
  const motionProps = animate ? {
    initial: { scale: 0, y: -20 },
    animate: { scale: 1, y: 0 },
    transition: { duration: 0.7, ease: [0.4, 0, 0.2, 1] },
    whileHover: { scale: 1.1, y: -2 }
  } : {};

  return (
    <IconComponent
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      {...motionProps}
    >
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
      <circle cx="12" cy="12" r="2" fill="currentColor" opacity="0.3" />
    </IconComponent>
  );
};

// Pulse Icon - Health monitoring
export const PulseIcon = ({ className = '', animate = true }) => {
  const IconComponent = animate ? motion.svg : 'svg';
  
  const motionProps = animate ? {
    initial: { pathLength: 0 },
    animate: { pathLength: 1 },
    transition: { duration: 1.5, ease: [0.4, 0, 0.2, 1] }
  } : {};

  return (
    <IconComponent
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <motion.path 
        d="M22 12h-4l-3 9L9 3l-3 9H2" 
        {...motionProps}
      />
    </IconComponent>
  );
};

// Balance Icon - Ayurvedic balance
export const BalanceIcon = ({ className = '', animate = true }) => {
  const IconComponent = animate ? motion.svg : 'svg';
  
  const motionProps = animate ? {
    initial: { rotate: -10, scale: 0.9 },
    animate: { rotate: 0, scale: 1 },
    transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] },
    whileHover: { rotate: 2 }
  } : {};

  return (
    <IconComponent
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      {...motionProps}
    >
      <path d="M9 12l2 2 4-4" />
      <path d="M21 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
      <path d="M3 12c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
      <path d="M12 21c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
      <path d="M12 3c.552 0 1-.448 1-1s-.448-1-1-1-1 .448-1 1 .448 1 1 1z" />
      <circle cx="12" cy="12" r="9" />
    </IconComponent>
  );
};

// Animated Background Elements
export const FloatingLeaves = ({ className = '' }) => {
  const leaves = Array.from({ length: 6 }, (_, i) => i);

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
      {leaves.map((leaf) => (
        <motion.div
          key={leaf}
          className="absolute text-ayurveda-sage-100"
          style={{
            left: `${Math.random() * 100}%`,
          }}
          initial={{
            y: '100vh',
            rotate: 0,
            scale: 0.5 + Math.random() * 0.5
          }}
          animate={{
            y: '-10vh',
            rotate: 360,
          }}
          transition={{
            duration: 15 + Math.random() * 10,
            repeat: Infinity,
            ease: "linear",
            delay: Math.random() * 5
          }}
        >
          <LeafIcon className="w-8 h-8" animate={false} />
        </motion.div>
      ))}
    </div>
  );
};